import type { ClientVacationPeriod } from '../../../../models/vacationClientView.types';
import type { ConflictPersonItem } from './clientPeriodCard.types';
import { isScheduledColleague } from '../../vacationClientPanel.definitions';
import { colleagueRowKey } from '../../vacationClientPanel.calendar';

interface PeopleAggregator {
    collaboratorId: string;
    name: string;
    photoUrl: string | null;
    type: 'overlap' | 'nearby' | 'scheduled' | 'onVacation';
    colleagueRanges: Array<{ start: Date; end: Date }>;
    overlapRanges: Array<{ start: Date; end: Date; days: number; segmentOrder: number }>;
    nearbyGaps: Array<{ segmentOrder: number; gapDays: number; direction: 'before' | 'after' }>;
    overlapDays: number;
    gapDays: number;
    direction: 'before' | 'after' | null;
}

export function buildConflictPeople(
    period: ClientVacationPeriod,
    siblingPeriods: ClientVacationPeriod[] = []
): ConflictPersonItem[] {
    const peopleMap = new Map<string, PeopleAggregator>();

    period.overlaps.forEach((overlap) => {
        const key = colleagueRowKey(overlap.collaboratorId, overlap.colleagueStatus);
        const isOnVacation = overlap.colleagueStatus === 'OnVacation';
        const isScheduled = isScheduledColleague(overlap.colleagueStatus);

        let type: 'overlap' | 'nearby' | 'scheduled' | 'onVacation' = 'nearby';
        if (isOnVacation) {
            type = 'onVacation';
        } else if (isScheduled) {
            type = 'scheduled';
        } else if (overlap.proximity === 'overlap') {
            type = 'overlap';
        }

        const sibling = siblingPeriods.find((p) => p.collaboratorId === overlap.collaboratorId);

        const entry = peopleMap.get(key) ?? {
            collaboratorId: overlap.collaboratorId,
            name: overlap.collaboratorName,
            photoUrl: sibling?.collaboratorPhotoUrl ?? null,
            type,
            colleagueRanges: [],
            overlapRanges: [],
            nearbyGaps: [],
            overlapDays: 0,
            gapDays: overlap.gapDays,
            direction: overlap.direction,
        };

        if (type === 'overlap') {
            entry.type = 'overlap';
        }

        entry.colleagueRanges.push({ start: overlap.colleagueStart, end: overlap.colleagueEnd });
        if (overlap.overlapStart && overlap.overlapEnd) {
            entry.overlapRanges.push({
                start: overlap.overlapStart,
                end: overlap.overlapEnd,
                days: overlap.overlapDays,
                segmentOrder: overlap.segmentOrder,
            });
        }
        if (type === 'nearby' && overlap.direction) {
            entry.nearbyGaps.push({ segmentOrder: overlap.segmentOrder, gapDays: overlap.gapDays, direction: overlap.direction });
        }
        entry.overlapDays += overlap.overlapDays;

        if (overlap.gapDays > 0) {
            entry.gapDays = overlap.gapDays;
            entry.direction = overlap.direction;
        }

        peopleMap.set(key, entry);
    });


    return Array.from(peopleMap.entries())
        .map(([key, entry]) => ({
            key,
            ...entry,
        }))
        .sort((a, b) => {
            const typeOrder: Record<string, number> = {
                overlap: 0,
                nearby: 1,
                onVacation: 2,
                scheduled: 3,
            };
            if (typeOrder[a.type] !== typeOrder[b.type]) {
                return typeOrder[a.type] - typeOrder[b.type];
            }
            return b.overlapDays - a.overlapDays;
        });
}
